import { z } from 'zod';
import { useQuery, useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { useAppStore } from '@/stores/app-store';
import { useInvalidateNotifications } from '@/hooks/use-invalidate-notifications';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import { creditThresholdsFormSchema } from '@/features/payments/types/form-schemas';
import type {
  BillingBalance,
  StoredPaymentMethod,
  PaymentMethodsResponse,
  SetupIntentResponse,
  CreditThresholds,
  TopUpCreditResponse,
  ChargePreview,
} from '@/features/payments/types/payments';

const FALLBACK_WHOLESALER_SCOPE = 'default';

// ─── Zod schemas for mutation inputs ─────────────────────────────────────────

export const TopUpCreditSchema = z.object({
  amountUsd: z.number().positive(),
  paymentMethodId: z.string().optional(),
  isAutoTopUp: z.boolean().optional(),
  netMode: z.boolean(),
});
export type TopUpCreditPayload = z.infer<typeof TopUpCreditSchema>;

export const ChargePreviewSchema = z.object({
  amountUsd: z.number().positive(),
  paymentMethodId: z.string().min(1),
  netMode: z.boolean(),
});
export type ChargePreviewPayload = z.infer<typeof ChargePreviewSchema>;

export const UpdatePaymentMethodSchema = z.object({
  setupIntentId: z.string().min(1),
  nameOnCard: z.string().min(1),
});
export type UpdatePaymentMethodPayload = z.infer<typeof UpdatePaymentMethodSchema>;

// Reuses the credit-thresholds-card form schema (the same three numeric fields) and adds
// the autoTopUp flag, which is managed outside the TanStack Form as a plain boolean toggle.
export const UpdateCreditThresholdsSchema = creditThresholdsFormSchema.extend({
  autoTopUp: z.boolean(),
});
export type UpdateCreditThresholdsPayload = z.infer<typeof UpdateCreditThresholdsSchema>;

// ─── Fetchers ─────────────────────────────────────────────────────────────────

export const fetchBalance = async (wholesalerScope: string): Promise<BillingBalance> => {
  const { data } = await api.get<BillingBalance>(API_ROUTES.billing.balance, {
    headers: { 'x-wholesaler-id': wholesalerScope },
  });
  return data;
};

export const fetchPaymentMethod = async (
  wholesalerScope: string,
): Promise<StoredPaymentMethod | null> => {
  try {
    const { data } = await api.get<StoredPaymentMethod>(API_ROUTES.billing.paymentMethod, {
      headers: { 'x-wholesaler-id': wholesalerScope },
    });
    return data;
  } catch (err) {
    // 404 means no payment method on file — treat as empty, not an error
    if (
      err != null &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      return null;
    }
    throw err;
  }
};

export const fetchPaymentMethods = async (
  wholesalerScope: string,
): Promise<PaymentMethodsResponse> => {
  const { data } = await api.get<PaymentMethodsResponse>(API_ROUTES.billing.paymentMethods, {
    headers: { 'x-wholesaler-id': wholesalerScope },
  });
  return data;
};

export const setDefaultPaymentMethod = async (id: string): Promise<StoredPaymentMethod> => {
  const { data } = await api.post<StoredPaymentMethod>(
    API_ROUTES.billing.setDefaultPaymentMethod(id),
  );
  return data;
};

export const deletePaymentMethod = async (id: string): Promise<void> => {
  await api.delete(API_ROUTES.billing.deletePaymentMethod(id));
};

export const fetchSetupIntent = async (companyName?: string): Promise<SetupIntentResponse> => {
  const { data } = await api.post<SetupIntentResponse>(API_ROUTES.billing.setupIntent, {
    allowCreate: false,
    companyName,
  });
  return data;
};

export const updatePaymentMethod = async (
  payload: UpdatePaymentMethodPayload,
): Promise<StoredPaymentMethod> => {
  const { data } = await api.put<StoredPaymentMethod>(API_ROUTES.billing.paymentMethod, payload);
  return data;
};

export const fetchCreditThresholds = async (
  wholesalerScope: string,
): Promise<CreditThresholds | null> => {
  try {
    const { data } = await api.get<CreditThresholds>(API_ROUTES.billing.creditThresholds, {
      headers: { 'x-wholesaler-id': wholesalerScope },
    });
    return data;
  } catch (err) {
    // 404 means thresholds were never saved for this wholesaler — treat as unset, not an error
    if (
      err != null &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      return null;
    }
    throw err;
  }
};

export const updateCreditThresholds = async (
  wholesalerScope: string,
  payload: UpdateCreditThresholdsPayload,
): Promise<CreditThresholds> => {
  const { data } = await api.put<CreditThresholds>(API_ROUTES.billing.creditThresholds, payload, {
    headers: { 'x-wholesaler-id': wholesalerScope },
  });
  return data;
};

// Note: wholesaler-setup/api/billing.ts has its own fetchChargePreview with a different
// request/response shape for the same route (used pre-activation, before a stored payment
// method exists) — not reconciled here since the two call sites have different needs.
export const fetchChargePreview = async (payload: ChargePreviewPayload): Promise<ChargePreview> => {
  const { data } = await api.post<ChargePreview>(API_ROUTES.billing.chargePreview, payload);
  return data;
};

export const topUpCredit = async (
  wholesalerId: string,
  payload: TopUpCreditPayload,
): Promise<TopUpCreditResponse> => {
  const { data } = await api.post<TopUpCreditResponse>(
    API_ROUTES.billing.wholesalerCredit(wholesalerId),
    payload,
  );
  return data;
};

// ─── Query options factories ───────────────────────────────────────────────────

export const getBalanceQueryOptions = (wholesalerScope: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.billing.balance(wholesalerScope),
    queryFn: () => fetchBalance(wholesalerScope),
    staleTime: 0,
    gcTime: 0,
  });

export const getPaymentMethodQueryOptions = (wholesalerScope: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.billing.paymentMethod(wholesalerScope),
    queryFn: () => fetchPaymentMethod(wholesalerScope),
  });

export const getPaymentMethodsQueryOptions = (wholesalerScope: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.billing.paymentMethods(wholesalerScope),
    queryFn: () => fetchPaymentMethods(wholesalerScope),
  });

export const getCreditThresholdsQueryOptions = (wholesalerScope: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.billing.creditThresholds(wholesalerScope),
    queryFn: () => fetchCreditThresholds(wholesalerScope),
  });

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useBalance = () => {
  const wholesalerId = useAppStore(
    (state) => state.selectedWholesalerId ?? state.currentUser?.wholesalers[0]?.id ?? null,
  );
  return useQuery({
    ...getBalanceQueryOptions(wholesalerId ?? FALLBACK_WHOLESALER_SCOPE),
    enabled: wholesalerId != null,
  });
};

export const usePaymentMethod = () => {
  const wholesalerId = useAppStore(
    (state) => state.selectedWholesalerId ?? state.currentUser?.wholesalers[0]?.id ?? null,
  );
  return useQuery({
    ...getPaymentMethodQueryOptions(wholesalerId ?? FALLBACK_WHOLESALER_SCOPE),
    enabled: wholesalerId != null,
  });
};

export const usePaymentMethods = () => {
  const wholesalerId = useAppStore(
    (state) => state.selectedWholesalerId ?? state.currentUser?.wholesalers[0]?.id ?? null,
  );
  return useQuery({
    ...getPaymentMethodsQueryOptions(wholesalerId ?? FALLBACK_WHOLESALER_SCOPE),
    enabled: wholesalerId != null,
  });
};

export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const firstWholesalerId = useAppStore((state) => state.currentUser?.wholesalers[0]?.id);
  const wholesalerScope = selectedWholesalerId ?? firstWholesalerId ?? FALLBACK_WHOLESALER_SCOPE;
  return useMutation({
    mutationFn: (id: string) => setDefaultPaymentMethod(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.paymentMethods(wholesalerScope),
      });
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.dashboard.billing.toast.defaultPaymentMethodSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.dashboard.billing.toast.defaultPaymentMethodError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useDeletePaymentMethod = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const firstWholesalerId = useAppStore((state) => state.currentUser?.wholesalers[0]?.id);
  const wholesalerScope = selectedWholesalerId ?? firstWholesalerId ?? FALLBACK_WHOLESALER_SCOPE;
  return useMutation({
    mutationFn: (id: string) => deletePaymentMethod(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.paymentMethods(wholesalerScope),
      });
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.dashboard.billing.toast.paymentMethodRemoved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.dashboard.billing.toast.paymentMethodRemoveError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useCreateSetupIntent = () =>
  useMutation({
    mutationFn: fetchSetupIntent,
  });

export const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const firstWholesalerId = useAppStore((state) => state.currentUser?.wholesalers[0]?.id);
  const wholesalerScope = selectedWholesalerId ?? firstWholesalerId ?? FALLBACK_WHOLESALER_SCOPE;
  return useMutation({
    mutationFn: updatePaymentMethod,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.paymentMethod(wholesalerScope),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.paymentMethods(wholesalerScope),
      });
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.dashboard.billing.toast.paymentMethodSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.dashboard.billing.toast.paymentMethodSaveError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useCreditThresholds = () => {
  const wholesalerId = useAppStore(
    (state) => state.selectedWholesalerId ?? state.currentUser?.wholesalers[0]?.id ?? null,
  );
  return useQuery({
    ...getCreditThresholdsQueryOptions(wholesalerId ?? FALLBACK_WHOLESALER_SCOPE),
    enabled: wholesalerId != null,
  });
};

export const useUpdateCreditThresholds = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const currentUser = useAppStore((state) => state.currentUser);
  const wholesalerId = selectedWholesalerId ?? currentUser?.wholesalers[0]?.id;
  const wholesalerScope = wholesalerId ?? FALLBACK_WHOLESALER_SCOPE;
  return useMutation({
    mutationFn: (payload: UpdateCreditThresholdsPayload) =>
      updateCreditThresholds(wholesalerScope, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.creditThresholds(wholesalerScope),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.balance(wholesalerScope),
      });
      if (wholesalerId != null) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.overview.byWholesaler(wholesalerId),
        });
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.billing.wholesalerCredit(wholesalerId),
        });
      }
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.dashboard.billing.toast.creditThresholdsSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.dashboard.billing.toast.creditThresholdsError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useCreateChargePreview = () =>
  useMutation({
    mutationFn: fetchChargePreview,
  });

export const useTopUpCredit = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const firstWholesalerId = useAppStore((state) => state.currentUser?.wholesalers[0]?.id);
  const wholesalerId = selectedWholesalerId ?? firstWholesalerId ?? null;
  const wholesalerScope = wholesalerId ?? FALLBACK_WHOLESALER_SCOPE;

  return useMutation({
    mutationFn: (payload: TopUpCreditPayload) => {
      if (wholesalerId == null) return Promise.reject(new Error('No wholesaler selected'));
      return topUpCredit(wholesalerId, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.balance(wholesalerScope),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.billing.paymentMethods(wholesalerScope),
      });
      if (wholesalerId != null) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.overview.byWholesaler(wholesalerScope),
        });
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.billing.wholesalerCredit(wholesalerScope),
        });
      }
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.dashboard.billing.toast.topUpSuccess });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.dashboard.billing.toast.topUpError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
